import { merchantsDraft, type ConditionDraft, type RuleDraft } from '../rules/draft'
import type { Txn } from './transactionWire'

const isComma = (m: string) => m.includes(',')

const merchantCondition = (op: ConditionDraft['op'], names: string[]): ConditionDraft => ({
  field: 'merchant_name',
  op,
  value: names[0] ?? '',
  value2: '',
  list: names,
})

/**
 * The rule that "Create rule from these" opens (Q18).
 *
 * The rule's condition is `merchant_name`, matched against the displayed
 * merchant, so it takes each row's `merchant` — never `raw`, the bank's
 * descriptor, which is a different field. Duplicates collapse, first seen first.
 *
 * `set_category` is pre-filled only when EVERY selected row has the same
 * category; a mix, or any uncategorised row, leaves it for the user to choose.
 *
 * An `is_in` list is written comma-separated, so a name holding a comma cannot
 * live in one without becoming two. `equals` takes the whole name, so nothing is
 * dropped: a lone merchant is just `equals`, and with several the comma-free
 * names stay in one `is_in` while each comma-bearing name gets its own `equals`,
 * all in ONE `or` group (the backend's `ConditionGroupCreate.logic_operator`
 * accepts `or`; conditions within a group are alternatives).
 */
export function ruleSeed(rows: readonly Txn[]): RuleDraft | null {
  const all = [...new Set(rows.map((r) => r.merchant.trim()).filter((m) => m !== ''))]
  if (all.length === 0) return null
  const categories = new Set(rows.map((r) => r.categoryId))
  const shared = categories.size === 1 ? ([...categories][0] ?? null) : null
  const base = merchantsDraft(all, shared)
  if (all.length === 1 || !all.some(isComma)) return base

  const plain = all.filter((m) => !isComma(m))
  const conditions = [
    ...(plain.length === 0 ? [] : [merchantCondition(plain.length === 1 ? 'equals' : 'is_in', plain)]),
    ...all.filter(isComma).map((m) => merchantCondition('equals', [m])),
  ]
  return { ...base, groups: [{ join: 'or', conditions }] }
}
